import Popup from 'reactjs-popup'
import { useEffect, useRef } from 'react'
import { useWindowSize } from 'usehooks-ts'

import { type SanityImageFragment } from '@data/sanity/queries/types/image'

import ModalGalleryCloseButton from './close-button'
import ModalGalleryImage from './image'

interface ModalGalleryProps {
  isModalOpen: boolean
  images: SanityImageFragment[]
  initialImageIndex: number | null
  onClose: () => void
}

const ModalGallery = ({
  isModalOpen,
  images,
  initialImageIndex,
  onClose,
}: ModalGalleryProps) => {
  const windowSize = useWindowSize()

  const modalGalleryImagesRef = useRef<HTMLDivElement | null>(null)
  const lastInitialImageIndexRef = useRef<number | null>(null)

  // Scroll to initial image when opening modal
  useEffect(() => {
    if (!isModalOpen) {
      lastInitialImageIndexRef.current = null
      return
    }

    if (lastInitialImageIndexRef.current === initialImageIndex) {
      return
    }

    lastInitialImageIndexRef.current = initialImageIndex

    if (initialImageIndex === null) {
      return
    }

    // Calculate image heights above the selected image
    const imageHeights = images.map((image) => {
      const aspectRatio = image.asset?.metadata?.dimensions?.aspectRatio ?? 1
      const imageHeight = windowSize.width / aspectRatio

      return imageHeight
    })
    const imageTop = imageHeights
      .slice(0, initialImageIndex)
      .reduce((total, height) => total + height, 0)

    // Scroll to image
    setTimeout(() => {
      modalGalleryImagesRef.current?.scroll({
        top: imageTop,
      })
    }, 0)
  }, [images, initialImageIndex, isModalOpen, windowSize])

  return (
    <Popup
      modal
      open={isModalOpen}
      onClose={onClose}
      lockScroll
      closeOnDocumentClick
      closeOnEscape
      repositionOnResize
      overlayStyle={{
        backgroundColor: 'rgba(0,0,0,0.25)',
        cursor: 'zoom-out',
      }}
      contentStyle={{
        width: '100%',
        height: '100%',
        padding: '0',
        border: 'none',
        backgroundColor: 'transparent',
      }}
    >
      <ModalGalleryCloseButton onClose={onClose} />

      <div
        ref={modalGalleryImagesRef}
        className="absolute inset-0 flex flex-col overflow-y-auto no-scrollbar"
      >
        {images.map((image, index) => (
          <ModalGalleryImage
            key={index}
            image={image}
            alt=""
            onClose={onClose}
            className="flex-none"
          />
        ))}
      </div>
    </Popup>
  )
}

export default ModalGallery
